// de-front/de-dehub-admin/src/pages/billing/fee-policies/FeePolicyTabsLayout.tsx
import { Badge, Button, Tabs } from "@mantine/core";
import {
  IconBook2,
  IconCoins,
  IconHistory,
  IconPencilPlus,
  IconUsers,
} from "@tabler/icons-react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import useSWR from "swr";
import { TenantFeePolicyAssignmentApi } from "../../../apis";
import { PageLayout } from "../../../components/layout";
import { PermissionGate } from "../../../components/billing/PermissionGate";

const BASE = "/my-billing/fee-policy";

type TabKey = "policies" | "customers" | "editor" | "history" | "collected";

function pathnameToTab(pathname: string): TabKey {
  if (pathname.startsWith(`${BASE}/customers`)) return "customers";
  if (pathname.startsWith(`${BASE}/editor`)) return "editor";
  if (pathname.startsWith(`${BASE}/history`)) return "history";
  if (pathname.startsWith(`${BASE}/collected`)) return "collected";
  return "policies";
}

const FeePolicyTabsLayout = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const active: TabKey = pathnameToTab(pathname);

  // Tab badge: total customer count for the Харилцагчид tab. Use the same roster
  // source as the tab list (/bil/my/fee-policy-customers), not the named-assignment
  // count — otherwise it shows only the few assigned customers, not all of them.
  const { data: customersData } = useSWR("swr.my.customers-count", () =>
    TenantFeePolicyAssignmentApi.listCustomers({ page: 1, limit: 1 })
  );
  const customerCount: number = customersData?.total ?? 0;

  const onChange = (value: string | null) => {
    if (!value) return;
    if (value === "policies") navigate(BASE);
    else navigate(`${BASE}/${value}`);
  };

  return (
    <PageLayout
      title="Шимтгэлийн бодлого"
      subTitle="Танай харилцагчдаас авах шимтгэлийг тохируулна."
      extra={[
        <PermissionGate key="new-draft" code="BIL_011">
          <Button
            leftSection={<IconPencilPlus size={16} />}
            onClick={() => navigate(`${BASE}/editor`)}>
            Шинэ бодлого үүсгэх
          </Button>
        </PermissionGate>,
      ]}>
      <Tabs value={active} onChange={onChange} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="policies" leftSection={<IconBook2 size={16} />}>
            Бодлогууд
          </Tabs.Tab>
          <Tabs.Tab
            value="customers"
            leftSection={<IconUsers size={16} />}
            rightSection={
              customerCount > 0 ? (
                <Badge size="sm" color="brand" variant="light" radius="xl">
                  {customerCount}
                </Badge>
              ) : null
            }>
            Харилцагчид
          </Tabs.Tab>
          <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>
            Түүх
          </Tabs.Tab>
          <Tabs.Tab value="collected" leftSection={<IconCoins size={16} />}>
            Цуглуулсан
          </Tabs.Tab>
        </Tabs.List>

        <Outlet />
      </Tabs>
    </PageLayout>
  );
};

export default FeePolicyTabsLayout;
